import "@wa/tokens/tokens.css";
import "@wa/ui/styles.css";
import "./index.css";
import "./crm-brand.css";
import "./crm-topbar.css";
import "./crm-tables.css";
import "./crm-register.css";
import "./crm-projects.css";
import "./crm-vault.css";
import "./crm-guides.css";
import "./crm-guides-editor.css";
import "./crm-reports.css";
import "./crm-ai-live.css";
import "./crm-folded-rows.css";
import "./crm-compose-editor.css";

import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Loader, bindScrollbarKind } from "@wa/ui";
import { I18nProvider } from "./i18n/I18nProvider";
import { BrandingProvider } from "./components/BrandingProvider";
import { getMe, isPlatformStaff, type StaffUser } from "./lib/auth";
import { bindThemeModeEffects } from "./lib/themeMode";
import { LoginPage } from "./pages/LoginPage";
import { CrmLayout } from "./crm/CrmLayout";
import { AccessDenied } from "./crm/AccessDenied";
import { AppErrorBoundary } from "./crm/shell/ErrorBoundary";

function Splash() {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-background">
      <Loader size="lg" />
    </div>
  );
}

function App() {
  const [user, setUser] = useState<StaffUser | null | "loading">("loading");

  useEffect(() => {
    getMe().then(setUser);
    const stopScrollbars = bindScrollbarKind();
    const stopTheme = bindThemeModeEffects();
    return () => {
      stopScrollbars();
      stopTheme();
    };
  }, []);

  if (user === "loading") return <Splash />;
  if (!user) return <LoginPage onSuccess={(u) => setUser(u)} />;
  if (!isPlatformStaff(user)) return <AccessDenied user={user} onLogout={() => setUser(null)} />;
  return <CrmLayout user={user} onLogout={() => setUser(null)} />;
}

const container = document.getElementById("root")! as HTMLElement & {
  __root?: ReturnType<typeof createRoot>;
};
const root = container.__root ?? (container.__root = createRoot(container));
root.render(
  <StrictMode>
    <BrowserRouter>
      <I18nProvider>
        <BrandingProvider>
          {/* Inside I18nProvider so the fallback can be translated, and outside
              App so it still catches the header, the rail and the providers -
              everything the route-level boundary sits below. */}
          <AppErrorBoundary>
            <App />
          </AppErrorBoundary>
        </BrandingProvider>
      </I18nProvider>
    </BrowserRouter>
  </StrictMode>,
);
